'use client';
import React, { useState, useRef, ChangeEvent } from 'react';
import { Upload, Music, X, ZoomIn, ZoomOut } from 'lucide-react';
import { OpenSheetMusicDisplay } from 'opensheetmusicdisplay';

const SheetMusic: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [zoom, setZoom] = useState<number>(1.0);
  const containerRef = useRef<HTMLDivElement>(null);
  const osmdInstance = useRef<OpenSheetMusicDisplay | null>(null);

  const cleanMusicXML = (xmlContent: string): string => {
    // Remove Guitar Pro custom processing instructions
    let cleaned = xmlContent.replace(/<\?GP[\s\S]*?\?>/g, '');
    
    // Remove empty staff-details that might cause issues
    cleaned = cleaned.replace(/<staff-details[^>]*>\s*<\/staff-details>/g, '');
    
    return cleaned;
  };

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const uploadedFile = e.target.files?.[0];
    if (!uploadedFile) return;

    // Check file type
    const validTypes = ['.xml', '.musicxml', '.mxl'];
    const fileExtension = uploadedFile.name.toLowerCase().match(/\.[^.]+$/)?.[0];
    
    if (!fileExtension || !validTypes.includes(fileExtension)) {
      setError('Please upload a MusicXML file (.xml, .musicxml, or .mxl)');
      return;
    }

    setError('');
    setIsLoading(true);
    setFile(uploadedFile);

    try {
      const fileContent = await uploadedFile.text();
      const cleanedContent = cleanMusicXML(fileContent);
      await renderSheetMusic(cleanedContent);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError('Failed to load sheet music: ' + errorMessage);
      console.error('Error loading file:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const renderSheetMusic = async (xmlContent: string): Promise<void> => {
    // Wait for container to be ready
    let attempts = 0;
    while (!containerRef.current && attempts < 10) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }

    if (!containerRef.current) {
      setError('Container not ready. Please try again.');
      setIsLoading(false);
      return;
    }

    try {
      // Clear previous instance
      if (osmdInstance.current) {
        containerRef.current.innerHTML = '';
        osmdInstance.current = null;
      }

      // Create new OSMD instance
      const osmd = new OpenSheetMusicDisplay(containerRef.current, {
        autoResize: true,
        backend: 'svg',
        drawTitle: true,
        drawComposer: true,
        drawCredits: true,
      });

      await osmd.load(xmlContent);
      osmd.zoom = zoom;
      osmd.render();
      
      osmdInstance.current = osmd;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError('Error rendering sheet music: ' + errorMessage);
      console.error('Render error:', err);
      setIsLoading(false);
    }
  };

  const handleZoomIn = (): void => {
    const newZoom = Math.min(zoom + 0.2, 3.0);
    setZoom(newZoom);
    if (osmdInstance.current) {
      osmdInstance.current.zoom = newZoom;
      osmdInstance.current.render();
    }
  };

  const handleZoomOut = (): void => {
    const newZoom = Math.max(zoom - 0.2, 0.4);
    setZoom(newZoom);
    if (osmdInstance.current) {
      osmdInstance.current.zoom = newZoom;
      osmdInstance.current.render();
    }
  };

  const clearFile = (): void => {
    setFile(null);
    setError('');
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }
    osmdInstance.current = null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Music className="w-10 h-10 text-indigo-600" />
            <h1 className="text-4xl font-bold text-gray-800">Sheet Music Reader</h1>
          </div>
          <p className="text-gray-600">Upload MusicXML files to view interactive sheet music</p>
        </div>

        {/* Upload Section */}
        {!file && (
          <div className="max-w-2xl mx-auto">
            <label className="block cursor-pointer">
              <div className="border-2 border-dashed border-indigo-300 rounded-lg p-12 text-center hover:border-indigo-500 hover:bg-indigo-50 transition-all">
                <Upload className="w-16 h-16 mx-auto mb-4 text-indigo-500" />
                <p className="text-lg font-medium text-gray-700 mb-2">
                  Click to upload or drag and drop
                </p>
                <p className="text-sm text-gray-500">
                  Supports MusicXML (.xml, .musicxml, .mxl)
                </p>
              </div>
              <input
                type="file"
                className="hidden"
                accept=".xml,.musicxml,.mxl"
                onChange={handleFileUpload}
              />
            </label>

            {/* Sample File Info */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> You can find sample MusicXML files at{' '}
                <a 
                  href="https://www.musicxml.com/music-in-musicxml/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="underline hover:text-blue-600"
                >
                  musicxml.com
                </a>
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="max-w-2xl mx-auto mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent"></div>
            <p className="mt-4 text-gray-600">Loading sheet music...</p>
          </div>
        )}

        {/* Controls and Sheet Music Display */}
        {file && (
          <div className="space-y-4">
            {/* Control Bar */}
            {!isLoading && (
              <div className="bg-white rounded-lg shadow-md p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Music className="w-5 h-5 text-indigo-600" />
                  <span className="font-medium text-gray-700">{file.name}</span>
                </div>
                
                <div className="flex items-center gap-4">
                  {/* Zoom Controls */}
                  <div className="flex items-center gap-2 border-r pr-4">
                    <button
                      onClick={handleZoomOut}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Zoom Out"
                    >
                      <ZoomOut className="w-5 h-5 text-gray-700" />
                    </button>
                    <span className="text-sm text-gray-600 min-w-[60px] text-center">
                      {Math.round(zoom * 100)}%
                    </span>
                    <button
                      onClick={handleZoomIn}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Zoom In"
                    >
                      <ZoomIn className="w-5 h-5 text-gray-700" />
                    </button>
                  </div>

                  {/* Clear Button */}
                  <button
                    onClick={clearFile}
                    className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Clear
                  </button>
                </div>
              </div>
            )}

            {/* Sheet Music Container */}
            <div className="bg-white rounded-lg shadow-lg p-8 overflow-auto">
              <div ref={containerRef} className="min-h-[400px]"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SheetMusic;